import type {
    OrganisationTypeDto,
    OrganisationListItemDto,
    OrganisationUpsertDto,
} from "../models/Organisation";
import type {
    OrganisationUserDto,
    OrganisationUserRoleOptionDto,
    OrganisationUserUpsertDto
} from "../models/OrganisationUsers";
import { useApiClient } from "../components/useApiClient";

export function useOrganisationsApi() {
    const { get: getJson, putJson, postJson, delete: del } = useApiClient();

    async function getTypes(accessToken: string): Promise<OrganisationTypeDto[]> {
        return getJson<OrganisationTypeDto[]>("admin/organisations/types", accessToken);
    }

    async function list(
        accessToken: string,
        params?: { search?: string; organisationTypeId?: number | "" }
    ): Promise<OrganisationListItemDto[]> {
        const qs = new URLSearchParams();
        if (params?.search?.trim()) qs.append("search", params.search.trim());
        if (params?.organisationTypeId) qs.append("organisationTypeId", String(params.organisationTypeId));
        const query = qs.toString();
        return getJson<OrganisationListItemDto[]>(
            `admin/organisations${query ? `?${query}` : ""}`,
            accessToken
        );
    }

    async function create(
        dto: OrganisationUpsertDto,
        accessToken: string
    ): Promise<OrganisationUpsertDto> {
        return postJson<OrganisationUpsertDto>("admin/organisations", accessToken, dto);
    }

    async function update(
        id: number,
        dto: OrganisationUpsertDto,
        accessToken: string
    ): Promise<OrganisationUpsertDto> {
        return putJson<OrganisationUpsertDto>(`admin/organisations/${id}`, accessToken, dto);
    }

    async function deleteOrganisation(id: number, accessToken: string): Promise<void> {
        return del<void>(`admin/organisations/${id}`, accessToken);
    }

    // ── Organisation users ────────────────────────────────────────────────────

    async function getRoles(accessToken: string): Promise<OrganisationUserRoleOptionDto[]> {
        return getJson<OrganisationUserRoleOptionDto[]>("admin/organisations/roles", accessToken);
    }

    async function getUsers(
        organisationId: number,
        accessToken: string
    ): Promise<OrganisationUserDto[]> {
        return getJson<OrganisationUserDto[]>(
            `admin/organisations/${organisationId}/users`,
            accessToken
        );
    }

    async function addUser(
        organisationId: number,
        dto: OrganisationUserUpsertDto,
        accessToken: string
    ): Promise<OrganisationUserDto> {
        return postJson<OrganisationUserDto>(
            `admin/organisations/${organisationId}/users`,
            accessToken,
            dto
        );
    }

    async function updateUser(
        organisationId: number,
        organisationUserRoleId: number,
        dto: OrganisationUserUpsertDto,
        accessToken: string
    ): Promise<OrganisationUserDto> {
        return putJson<OrganisationUserDto>(
            `admin/organisations/${organisationId}/users/${organisationUserRoleId}`,
            accessToken,
            dto
        );
    }

    async function removeUser(
        organisationId: number,
        organisationUserRoleId: number,
        accessToken: string
    ): Promise<void> {
        return del<void>(
            `admin/organisations/${organisationId}/users/${organisationUserRoleId}`,
            accessToken
        );
    }

    return {
        getTypes,
        list,
        create,
        update,
        deleteOrganisation,
        getRoles,
        getUsers,
        addUser,
        updateUser,
        removeUser
    };
}